import { Chart as ChartJS } from 'chart.js'
import { defineComponent, h } from 'vue'
import _ from 'lodash'
import { registerChartComponents } from './chartSetup'
import { buildExternalTooltip } from './chartTooltip'

const parseChartNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value == null || typeof value === 'object') return value
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : value
}

export default defineComponent({
  name: 'BarChart',
  inheritAttrs: false,
  props: {
    chartData: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Object,
      default: () => ({})
    },
    styles: {
      type: [Object, Array, String],
      default: null
    },
    canvasId: {
      type: String,
      default: null
    },
    canvasClass: {
      type: [String, Array, Object],
      default: null
    },
    dataTypes: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      chart: null,
      renderFrame: null
    }
  },
  computed: {
    parsedChartData () {
      const dataClone = _.cloneDeep(this.chartData)
      if (dataClone && Array.isArray(dataClone.datasets)) {
        dataClone.datasets = dataClone.datasets.map(dataset => {
          if (Array.isArray(dataset.data)) {
            dataset.data = dataset.data.map(parseChartNumber)
          }

          if (this.dataTypes) {
            if (this.dataTypes[dataset.label] === 'percentage') {
              dataset.data = dataset.data.map(value => {
                const num = Number(value)
                return Number.isFinite(num) ? Math.round(num) : value
              })
            } else {
              dataset.data = dataset.data.map(value => {
                const num = Number(value)
                return Number.isFinite(num) ? Number(num.toFixed(2)) : value
              })
            }
          }
          return dataset
        })
      }
      return dataClone
    },
    optionsWithCustomTooltip () {
      const optionsCopy = _.cloneDeep(this.options)
      if (!optionsCopy.plugins) optionsCopy.plugins = {}
      if (!optionsCopy.plugins.tooltip) {
        optionsCopy.plugins.tooltip = {
          enabled: false,
          external: buildExternalTooltip((body) => {
            const num = Number(body)
            return Number.isNaN(num) ? body : `${num % 1 === 0 ? num + '%' : num}`
          })
        }
      }
      return optionsCopy
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        this.renderChart()
      }
    },
    options: {
      deep: true,
      handler () {
        this.renderChart()
      }
    }
  },
  mounted () {
    registerChartComponents()
    this.renderChart()
  },
  beforeUnmount () {
    if (this.renderFrame) {
      cancelAnimationFrame(this.renderFrame)
      this.renderFrame = null
    }
    this.destroyChart()
  },
  methods: {
    destroyChart () {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },
    renderChart () {
      if (this.renderFrame) {
        cancelAnimationFrame(this.renderFrame)
      }
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null

        const canvas = this.$refs.canvas
        if (!canvas || !canvas.isConnected) return

        const context = canvas.getContext && canvas.getContext('2d')
        if (!context) return

        const chartData = this.parsedChartData
        if (!chartData || !Array.isArray(chartData.labels) || !chartData.labels.length) {
          this.destroyChart()
          return
        }

        this.destroyChart()

        const options = _.cloneDeep(this.optionsWithCustomTooltip || {})
        if (typeof options.animation === 'undefined') {
          options.animation = false
        }

        this.chart = new ChartJS(context, {
          type: 'bar',
          data: chartData,
          options
        })
      })
    }
  },
  render () {
    const attrs = { ...this.$attrs }
    const wrapperStyle = attrs.style
    delete attrs.style
    return h(
      'div',
      {
        ...attrs,
        style: [
          { position: 'relative', width: '100%', height: '300px' },
          this.styles,
          wrapperStyle
        ]
      },
      [h('canvas', {
        ref: 'canvas',
        id: this.canvasId || undefined,
        class: this.canvasClass || undefined
      })]
    )
  }
})
